import { z } from "zod";
import { DEFAULT_COUNTRY, isValidPhone } from "@/lib/phone";
import type { CountryCode } from "libphonenumber-js/max";
import { password, existingPassword } from "@/schemas/auth";

/** Optional free-text: empty is valid, but a value that is present must be
 *  substantial enough to be real. */
const optionalText = (min: number, message: string) =>
  z.string().trim().refine(v => v === "" || v.length >= min, message);

export const myProfileSchema = z.object({
  firstName: z.string().trim().min(2, "First name is required."),
  lastName: z.string().trim().min(2, "Last name is required."),
  // Optional. When set, notification emails are CC'd here.
  secondaryEmail: z.string().trim().email("Enter a valid email address.").or(z.literal("")),
  // Optional, but no longer "any seven characters". This field is a delivery
  // address now, not a note to self — an unparseable number here would be saved
  // and then silently never texted, which reads as the feature being broken.
  phone: z.string().trim(),
  /** ISO country the number is read against. Local, never sent to the server,
   *  which only ever stores E.164. Required rather than defaulted: `.default()`
   *  makes zod's *input* type optional, which no longer matches the resolver
   *  `useForm<MyProfileValues>` is typed with. `defaultValues` supplies it. */
  phoneCountry: z.string().trim().min(2),
  address: optionalText(5, "Enter a valid address."),
  emailNotifications: z.boolean(),
  smsNotifications: z.boolean(),
}).superRefine((values, ctx) => {
  if (!values.phone) return;  // clearing the number stays a legitimate edit
  if (!isValidPhone(values.phone, (values.phoneCountry || DEFAULT_COUNTRY) as CountryCode)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["phone"],
      message: "That isn't a valid number for the selected country.",
    });
  }
});

export const reminderSettingsSchema = z.object({
  rules: z.array(z.object({
    recipient: z.enum(["agent", "client"]),
    days_before: z.coerce
      .number()
      .int("Use whole days.")
      .min(0, "Use 0 or more days.")
      .max(30, "Use 30 days or fewer."),
    enabled: z.boolean(),
  })),
});

export const brokerageProfileSchema = z.object({
  // Only the display name is required — it is how the org is shown everywhere.
  legalName: optionalText(2, "Enter a valid legal name."),
  displayName: z.string().trim().min(2, "Display name is required."),
  supportEmail: z.string().trim().email("Enter a valid email address.").or(z.literal("")),
  phone: optionalText(7, "Enter a valid phone number."),
  licenseNumber: optionalText(3, "Enter a valid license number."),
  address: optionalText(5, "Enter a valid address."),
});

export const changePasswordSchema = z.object({
  // Required by the server: a live session token is not proof of identity for a
  // credential change, so the current password is re-checked.
  currentPassword: existingPassword,
  password,
  confirmPassword: z.string().min(1, "Confirm your new password."),
}).refine(({ password, confirmPassword }) => password === confirmPassword, {
  message: "Passwords don't match.",
  path: ["confirmPassword"],
}).refine(({ currentPassword, password }) => currentPassword !== password, {
  message: "Choose a password you haven't used here before.",
  path: ["password"],
});

export type MyProfileValues = z.infer<typeof myProfileSchema>;
export type ReminderSettingsValues = z.infer<typeof reminderSettingsSchema>;
export type BrokerageProfileValues = z.infer<typeof brokerageProfileSchema>;
export type ChangePasswordValues = z.infer<typeof changePasswordSchema>;
